/**
 * Shape EXACTO del payload que llega por `/topic/chat.{tenant}.{chatId}`.
 *
 * Reutilizado tal cual de dungo-front:
 *   dungo-front/app/src/app/core/models/websocket.models.ts  (interface WsMessageEvent)
 *   parseado en websocket.service.ts -> watchChat():
 *     JSON.parse(message.body) as WsMessageEvent
 *
 * Cómo distinguimos remitente (contacto vs agente IA/humano):
 *   - ambos null                       -> mensaje del propio CONTACTO (eco entrante).
 *   - `tipoAgenteIdDun === 'AGENTE_AI'`     -> mensaje de un AGENTE IA.
 *   - `tipoAgenteIdDun === 'AGENTE_HUMANO'` -> mensaje de un AGENTE HUMANO.
 *   OJO: `agenteIdDun != null` NO implica humano; los mensajes de la IA también lo
 *   traen (es el agente dueño del cerebro). El widget solo necesita "ambos null =
 *   propio" (ver directionOf en widget.ts), pero la distinción IA/humano se hace
 *   por el VALOR de `tipoAgenteIdDun`.
 *   `name` trae el nombre legible del remitente.
 */
export interface WsMessageEvent {
  conversacionIdDun: string;
  tenantIdDun: string;
  chatIdDun: string;
  contactoIdDun: string;
  /** `'AGENTE_AI'` | `'AGENTE_HUMANO'` | null (null = mensaje del propio contacto). */
  tipoAgenteIdDun: string | null;
  mensaje: string;
  fechaHoraMsj: string;
  agenteIdDun: string | null;
  name: string;
  contactoChannel: string;
  /** Tipo de mensaje multimedia. `"IMAGEN"` -> `<img>`; `"DOCUMENTO"` -> chip de descarga; null/ausente -> texto. */
  tipoMensaje?: string | null;
  /** URL lista para `<img src>`/`<a download>` cuando hay media (presigned, TTL ~24h). */
  mediaUrl?: string | null;
  /** MIME del media (ej. `image/jpeg`, `application/pdf`). */
  mimeType?: string | null;
  /** Nombre original del archivo (ej. `cotizacion-marzo.pdf`); solo presente en DOCUMENTO. */
  mediaFilename?: string | null;
}

/**
 * Evento de cambio de ESTADO del chat por el mismo topic
 * `/topic/chat.{tenant}.{chatId}`. NO trae `mensaje`: se discrimina por
 * `tipoEvento` para NO pintarlo como un mensaje normal.
 *
 *   { "tipoEvento":"ESTADO", "chatId":"…", "estado":"RESUELTA", "fechaHoraMsj":"…" }
 *
 * Cuando `estado === "RESUELTA"` el widget da la conversación por finalizada
 * y vuelve al estado `idle`.
 */
export interface WsStateEvent {
  tipoEvento: 'ESTADO';
  chatId: string;
  estado: string;
  fechaHoraMsj: string;
}

/**
 * Cualquier payload que llega por el topic. Los mensajes normales NO traen
 * `tipoEvento`; los eventos de estado sí. Se discrimina por esa propiedad.
 */
export type WsTopicEvent = WsMessageEvent | WsStateEvent;

/** Discriminador: ¿es un evento de estado (y no un mensaje normal)? */
export function isStateEvent(e: WsTopicEvent): e is WsStateEvent {
  return (e as Partial<WsStateEvent>).tipoEvento === 'ESTADO';
}

/**
 * Body del 409 que devuelve `POST /native/messages` cuando el chat ya está
 * resuelto. Se trata igual que el evento realtime RESUELTA: aviso + idle.
 */
export interface ChatResueltoError {
  code: 'CHAT_RESUELTO';
  [k: string]: unknown;
}

/**
 * Error lanzado por el transporte cuando el envío recibe 409 CHAT_RESUELTO.
 * El widget lo detecta para volver a idle sin reintentar el envío.
 */
export class ChatResueltoException extends Error {
  readonly code = 'CHAT_RESUELTO' as const;
  constructor(message = 'La conversación finalizó.') {
    super(message);
    this.name = 'ChatResueltoException';
  }
}

/**
 * Error lanzado por el transporte cuando el envío recibe 409 SESION_DESACTUALIZADA
 * (Plan 25): el token reservó un chat/contacto que otra sesión del mismo visitante ya
 * creó (p. ej. dos pestañas). El widget pide una sesión nueva y reenvía UNA vez.
 */
export class SesionDesactualizadaException extends Error {
  readonly code = 'SESION_DESACTUALIZADA' as const;
  constructor(message = 'La sesión del chat quedó desactualizada.') {
    super(message);
    this.name = 'SesionDesactualizadaException';
  }
}

/** Respuesta de POST {reciverBaseUrl}/native/messages */
export interface NativeMessageResponse {
  chatId: string;
  conversacionId: string;
}

/**
 * Respuesta 201 de POST {reciverBaseUrl}/native/media (subida de imagen).
 * Es el mensaje ya persistido, con la misma forma que un item de historial más
 * los campos multimedia. Se usa para pintar el eco propio de la imagen enviada.
 */
export interface NativeMediaResponse {
  conversacionId: string;
  mensaje: string;
  name: string | null;
  fechaHoraMsj: string;
  agenteIdDun: string | null;
  tipoAgenteIdDun: string | null;
  tipoMensaje: string | null;
  mediaUrl: string | null;
  mimeType: string | null;
  /** Nombre original del archivo; solo presente en DOCUMENTO. */
  mediaFilename?: string | null;
}

/**
 * Item del historial devuelto por GET {reciverBaseUrl}/native/messages?limit=N.
 *
 * La dirección se resuelve EXACTAMENTE igual que en `WsMessageEvent`:
 *   - ambos `null`                          -> mensaje del propio CONTACTO (lado propio).
 *   - `tipoAgenteIdDun === 'AGENTE_AI'`     -> mensaje de un AGENTE IA.
 *   - `tipoAgenteIdDun === 'AGENTE_HUMANO'` -> mensaje de un AGENTE HUMANO.
 * (`agenteIdDun` viene poblado en AMBOS casos: no sirve para distinguir IA de humano.)
 * Por eso reusamos el mismo painter que el realtime.
 */
export interface HistoryMessage {
  conversacionId: string;
  mensaje: string;
  name: string;
  fechaHoraMsj: string;
  agenteIdDun: string | null;
  /** `'AGENTE_AI'` | `'AGENTE_HUMANO'` | null (null = mensaje del propio contacto). */
  tipoAgenteIdDun: string | null;
  /**
   * Tipo de mensaje multimedia. `"IMAGEN"` -> `<img>`; `"DOCUMENTO"` -> chip; `"TEXTO"` -> texto.
   * En el historial la columna es NOT NULL DEFAULT 'TEXTO', así que aquí NO llega null
   * (a diferencia del topic, donde el eco del contacto viene con null).
   */
  tipoMensaje?: string | null;
  /** URL lista para `<img src>`/`<a download>` cuando hay media (presigned, TTL ~24h). */
  mediaUrl?: string | null;
  /** MIME del media (ej. `image/jpeg`, `application/pdf`). */
  mimeType?: string | null;
  /** Nombre original del archivo; solo presente en DOCUMENTO. */
  mediaFilename?: string | null;
}

export interface DungoWidgetTheme {
  /** Color primario (burbuja, header, burbujas salientes). Acepta cualquier valor CSS de color. */
  primaryColor?: string;
  /** Título mostrado en el header del panel. */
  title?: string;
}

/**
 * Sesión de contacto provista por el HOST. La obtiene su backend minteando
 * server-to-server contra dungo (usa un `api_secret` que NUNCA llega al
 * navegador) y devuelve el JWT de contacto resultante.
 */
export interface DungoWidgetSession {
  /** JWT de contacto acuñado por el backend anfitrión. */
  token: string;
  /** Opcional; si falta se decodifica del token (claim chat_id). */
  chatId?: string;
}

export interface DungoWidgetConfig {
  /** Base URL de dungo-reciver, ej: 'https://host:9051'. */
  reciverBaseUrl: string;
  /** Base URL de dungo-websocket, ej: 'https://host:9052'. Se le agrega '/ws' para SockJS. */
  wsUrl: string;
  /**
   * MODO PÚBLICO: base URL de dungo-backend (ej. 'https://dungo.ai') para el
   * mint público `POST /api/web/session/public`. Junto con `apiKey` activa el
   * formulario de pre-chat (el visitante se identifica solo). Si además hay
   * `token`/`getSession`, el modo embebido tiene prioridad.
   */
  backendBaseUrl?: string;
  /**
   * MODO PÚBLICO: api_key pública del channel NATIVO con `mint_publico`
   * habilitado. Es embebible en la página (NO es el api_secret); el backend
   * valida el `Origin` contra los dominios permitidos del tenant.
   */
  apiKey?: string;
  /** MODO PÚBLICO: código de país por defecto del selector de teléfono (default 'CL'). */
  defaultCountry?: string;
  /**
   * JWT de contacto acuñado por el backend anfitrión (server-to-server contra
   * /api/web/session). Opcional si se provee `getSession`. Si se pasan ambos,
   * `getSession` tiene prioridad para iniciar conversaciones nuevas.
   */
  token?: string;
  /**
   * Provisión de sesión bajo demanda. Función del HOST que llama a su backend
   * (el cual hace el mint contra dungo) y devuelve `{ token, chatId }`. Se
   * invoca cada vez que el contacto toca "Iniciar Conversación", de modo que
   * tras resolverse un chat se obtiene una sesión nueva. Prioritaria sobre
   * `token` cuando ambos están presentes.
   */
  getSession?: () => Promise<DungoWidgetSession>;
  /** Opcional; si falta se decodifica del token (claim chat_id). */
  chatId?: string;
  /** Opcional; si falta se decodifica del token (claim tenant). */
  tenant?: string;
  /**
   * Sección actual de la app del contacto (backward compat). Su valor viaja bajo
   * la clave `seccion` DENTRO del objeto de metainfo que el widget arma y envía
   * en el campo de red `seccion` de cada mensaje.
   */
  section?: string | Record<string, unknown>;
  /** Callback evaluado en cada envío; tiene prioridad sobre `section` (backward compat). */
  getSection?: () => string | Record<string, unknown>;
  /**
   * Metadatos arbitrarios del host, adjuntados bajo la clave `extra` del objeto
   * de metainfo enviado en cada mensaje. Útil para IDs de sesión de la app,
   * carrito, plan del usuario, etc. `getExtra` tiene prioridad si ambos están.
   */
  extra?: Record<string, unknown>;
  /**
   * Callback SÍNCRONO evaluado en CADA envío; tiene prioridad sobre `extra`.
   * Su retorno viaja bajo la clave `extra` del objeto de metainfo. Si lanza, se
   * ignora con gracia y no se rompe el envío.
   */
  getExtra?: () => Record<string, unknown>;
  theme?: DungoWidgetTheme;
}

/** Mensaje normalizado para el render interno de la UI. */
export interface UiMessage {
  text: string;
  /** 'out' = lo envió el contacto (este widget); 'in' = lo envió el agente (IA o humano). */
  direction: 'out' | 'in';
  /** Nombre legible del remitente (para mensajes entrantes). */
  name?: string;
  timestamp: string;
  /** Estado de envío para mensajes salientes optimistas. */
  status?: 'sending' | 'sent' | 'error';
  /** URL de la imagen a renderizar como `<img>`. Si está presente, la burbuja es de imagen. */
  imageUrl?: string;
  /**
   * Caption de la imagen/documento (texto bajo el media). Se omite si es el placeholder
   * `[imagen]`/`[documento]` que el backend usa cuando no hay caption.
   */
  caption?: string;
  /** URL de descarga del documento (`<a download>`). Si está presente, la burbuja es de documento. */
  docUrl?: string;
  /** Nombre original del archivo a mostrar en el chip de documento. */
  docName?: string;
  /** MIME del documento, para elegir el ícono del chip. */
  docMime?: string;
}

/** Instancia controlable del widget devuelta por init(). */
export interface DungoWidgetInstance {
  open(): void;
  close(): void;
  toggle(): void;
  destroy(): void;
}
